<script setup lang="ts">
import { ref } from "vue";
import type { RegistrationChannel } from "@/shared/@types/RegistrationChannel";
import ColumnLayout from "@/shared/components/ColumnLayout/ColumnLayout.vue";
import ColumnLayoutBox from "@/shared/components/ColumnLayoutBox/ColumnLayoutBox.vue";
import StatusMessagePage from "@/app/registration-app/shared/components/StatusMessagePage/StatusMessagePage.vue";
import type { InitialRegistrationData } from "@/app/registration-app/user/shared/@types/InitialRegistrationData";
import type { PersonalRegistrationData } from "@/app/registration-app/user/shared/@types/PersonalRegistrationData";
import type { PreRegistrationValidationData } from "@/app/registration-app/user/shared/@types/PreRegistrationValidationData";
import RegistrationWizard from "@/app/registration-app/user/shared/components/RegistrationWizard/RegistrationWizard.vue";
import { useValidatePreRegistrationData } from "./useValidatePreRegistrationData.composable";
import { useRegisterUser } from "./useRegisterUser.composable";

defineProps<{
  channel: RegistrationChannel;
  title: string;
  text: string;
  image: string;
  initialData?: InitialRegistrationData;
}>();

const emit = defineEmits<{
  (e: "request-invite"): void;
}>();

// --- Double Opt-In ---
const isDoubleOptInInfoVisible = ref(false);
const showDoubleOptInInfo = () => (isDoubleOptInInfoVisible.value = true);

const {
  isValidatingData,
  validatingDataError,
  isManagedCustomer,
  onValidatingPreRegistrationData,
} = useValidatePreRegistrationData();

const { isRegisteringUser, onRegisterUser } =
  useRegisterUser(showDoubleOptInInfo);

const preRegistrationValidationData = ref<
  PreRegistrationValidationData | undefined
>();
const currentStep = ref(1);
const showDocumentIdField = ref(false);

// Step 1: verify the customer number and email, then the document number
// once revealed, using the same validations endpoint. Managed customers
// can't self-register; other customers must provide the document number before
// continuing.
const verifyPreRegistrationData = async (
  data: PreRegistrationValidationData,
) => {
  await onValidatingPreRegistrationData(data);

  if (isManagedCustomer.value) {
    emit("request-invite");
    return;
  }

  if (validatingDataError.value) return;

  if (!showDocumentIdField.value) {
    showDocumentIdField.value = true;
    return;
  }

  preRegistrationValidationData.value = data;
  currentStep.value = 2;
};

// Step 2: Register the user with the pre-verified data and additional personal information
const registerUser = (personalRegistrationData: PersonalRegistrationData) => {
  if (!preRegistrationValidationData.value) return;

  onRegisterUser({
    ...personalRegistrationData,
    ...preRegistrationValidationData.value,
  });
};
</script>

<template>
  <StatusMessagePage
    v-if="isDoubleOptInInfoVisible"
    :title="$t('registration.user.doubleOptIn.title')"
  >
    <template #text>
      <p>{{ $t("registration.user.doubleOptIn.info") }}</p>
      <p>{{ $t("registration.user.doubleOptIn.info2") }}</p>
    </template>
  </StatusMessagePage>
  <ColumnLayout
    v-else
    :column-count="2"
  >
    <template #column1>
      <ColumnLayoutBox>
        <RegistrationWizard
          :current-step="currentStep"
          :title="title"
          :text="text"
          :channel="channel"
          :initial-data="initialData"
          :validating-data-error="validatingDataError"
          :is-validating-data="isValidatingData"
          :is-registering-user="isRegisteringUser"
          :show-document-id-field="showDocumentIdField"
          @validate-data="verifyPreRegistrationData"
          @register-user="registerUser"
        />
      </ColumnLayoutBox>
    </template>
    <template #column2>
      <img
        class="registration-wizard-container--image"
        :src="image"
        :alt="title"
      />
    </template>
  </ColumnLayout>
</template>

<style scoped>
@import "RegistrationWizardContainer.css";
</style>
